import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Loader2, Globe, ShieldOff, Power, Save } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

export default function AdminAccess() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ enabled: false, block_india: false, block_vpn: false });
  const [initial, setInitial] = useState({ enabled: false, block_india: false, block_vpn: false });
  const { toast } = useToast();

  useEffect(() => {
    api.get('/settings/access').then((r) => {
      const f = { enabled: !!r.data.enabled, block_india: !!r.data.block_india, block_vpn: !!r.data.block_vpn };
      setForm(f); setInitial(f);
    }).catch(() => toast({ title: 'Failed to load settings' })).finally(() => setLoading(false));
  }, []);

  const dirty = form.enabled !== initial.enabled || form.block_india !== initial.block_india || form.block_vpn !== initial.block_vpn;

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put('/settings/access', form);
      const f = { enabled: !!r.data.enabled, block_india: !!r.data.block_india, block_vpn: !!r.data.block_vpn };
      setForm(f); setInitial(f);
      toast({ title: 'Access settings updated' });
    } catch (e) {
      toast({ title: 'Update failed' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-14 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div data-testid="admin-access-page">
      <div className="mb-6">
        <h1 className="font-serif-display text-3xl font-bold text-slate-900">Access Control</h1>
        <p className="text-slate-600 mt-1">Block visitors by country or VPN/proxy. Admin pages and /unsubscribe are never blocked.</p>
      </div>

      <div className="space-y-4 max-w-2xl">
        {/* Master switch */}
        <div className={`rounded-xl border p-5 flex items-start gap-4 ${form.enabled ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${form.enabled ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
            <Power className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-900">Enable access restrictions</Label>
                <p className="text-xs text-slate-600 mt-1">Master toggle. When OFF, no visitors are blocked — the other rules below are ignored.</p>
              </div>
              <Switch data-testid="access-enabled-toggle" checked={form.enabled} onCheckedChange={(v) => setForm({ ...form, enabled: v })} />
            </div>
          </div>
        </div>

        {/* India */}
        <div className={`rounded-xl border p-5 flex items-start gap-4 ${!form.enabled ? 'opacity-60' : ''} ${form.block_india && form.enabled ? 'bg-red-50/40 border-red-200' : 'bg-white border-slate-200'}`}>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${form.block_india && form.enabled ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
            <Globe className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-900">Block Indian IP addresses</Label>
                <p className="text-xs text-slate-600 mt-1">Visitors with country code <code>IN</code> will see the "Access Restricted" page.</p>
              </div>
              <Switch data-testid="access-india-toggle" checked={form.block_india} disabled={!form.enabled} onCheckedChange={(v) => setForm({ ...form, block_india: v })} />
            </div>
          </div>
        </div>

        {/* VPN */}
        <div className={`rounded-xl border p-5 flex items-start gap-4 ${!form.enabled ? 'opacity-60' : ''} ${form.block_vpn && form.enabled ? 'bg-red-50/40 border-red-200' : 'bg-white border-slate-200'}`}>
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${form.block_vpn && form.enabled ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-500'}`}>
            <ShieldOff className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <div className="flex items-start justify-between gap-3">
              <div>
                <Label className="text-sm font-semibold text-slate-900">Block VPN / proxy / datacenter IPs</Label>
                <p className="text-xs text-slate-600 mt-1">Detects known VPN providers, proxies, Tor, and datacenter/hosting IPs (AWS, GCP, Azure, DigitalOcean, OVH, Hetzner, etc.).</p>
              </div>
              <Switch data-testid="access-vpn-toggle" checked={form.block_vpn} disabled={!form.enabled} onCheckedChange={(v) => setForm({ ...form, block_vpn: v })} />
            </div>
          </div>
        </div>

        <div className="pt-2 flex items-center gap-3">
          <Button data-testid="access-save-btn" onClick={save} disabled={!dirty || saving} className="bg-emerald-600 hover:bg-emerald-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-1.5" /> Save changes</>}
          </Button>
          {dirty && <span className="text-xs text-slate-500">Unsaved changes</span>}
        </div>

        <div className="mt-6 rounded-xl bg-slate-50 border border-slate-200 p-4 text-xs text-slate-600 leading-relaxed">
          <p className="font-semibold text-slate-800 mb-1">Notes</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>IP lookups are cached for 24 hours per unique visitor to reduce API calls.</li>
            <li>The admin panel (/admin/*) and the unsubscribe page (/unsubscribe) are <b>never</b> blocked — you can always reach them.</li>
            <li>Private / local IPs (server-side, dev) are never blocked.</li>
            <li>If the geo lookup fails, visitors are allowed (fail-open).</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
